/* eslint-disable import/no-extraneous-dependencies */
import { AecomCoreModule, LayoutService } from "@aecom/core";
import { CommonModule } from "@angular/common";
import { HttpClientModule, HTTP_INTERCEPTORS } from "@angular/common/http";
import { APP_INITIALIZER, NgModule } from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { BrowserModule } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import {
  MsalBroadcastService,
  MsalGuard,
  MsalGuardConfiguration,
  MsalInterceptor,
  MsalInterceptorConfiguration,
  MsalModule,
  MsalService,
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalRedirectComponent,
} from "@azure/msal-angular";
import {
  BrowserCacheLocation,
  InteractionType,
  IPublicClientApplication,
  LogLevel,
  PublicClientApplication,
} from "@azure/msal-browser";
import { BBJFileUrlProvider, AuthInterceptor, AuthService, BBJCoreModule, BBJFileListService, BBJSelectUserService } from "@bbj/components";
import { NgxSpinnerModule } from "ngx-spinner";
import { ApiModule } from "src/app/api-generated/api.module";
import environment from "src/environments/environment";
import { OAuthSettings } from "../oauth";
import { BASE_PATH } from "./api-generated";
import AppRoutingModule from "./app-routing.module";
import AppComponent from "./app.component";
import AccessDeniedComponent from "./content/auth/access-denied/access-denied.component";
import BaseComponent from "./content/base/base.component";
import ErrorComponent from "./content/error/error.component";
import CompilingRfiComponent from "./content/rfi/compiling-rfi/compiling-rfi.component";
import CreateRfiComponent from "./content/rfi/create-rfi/create-rfi.component";
import InreviewQaqcRfiComponent from "./content/rfi/inreview-qaqc-rfi/inreview-qaqc-rfi.component";
import ReportComponent from "./content/rfi/report/report.component";
import ReviewRfiComponent from "./content/rfi/review-rfi/review-rfi.component";
import RFIListComponent from "./content/rfi/rfi-list/rfi-list.component";
import ViewRFIComponent from "./content/rfi/view-rfi/view-rfi.component";
import { SignoutComponent } from "./content/signout/signout.component";
import { PendingChangesGuard } from "./guards/pending-changes.guard";
import ErrorResolve from "./resolvers/error-resolver";
import LoginUserResolve from "./resolvers/login-user-resolver";
import RFIViewResolver from "./resolvers/rfi-view.resolver";
import LocalClassificationService from "./services/local-classification.service";
import LocalContractRequirementService from "./services/local-contractRequirement.service";
import LocalContractService from "./services/local-contract.service";
import LocalContractUserService from "./services/local-contractUser.service";
import LocalSpecificationService from "./services/local-specification.service";
import LocalStorageService from "./services/local-storage.service";
import LocalUserService from "./services/local-user.service";
import NavService from "./services/nav.service";
import { DelegateModalComponent } from "./shared/delegate-modal/delegate-modal.component";
import { StorageKey } from "./shared/localstorage";
import NotifyListComponent from "./shared/notify-list/notify-list.component";
import { RecallModalComponent } from "./shared/recall-modal/recall-modal.component";
import { PreviousRouteService } from "./services/previousRoute.service";
import { UserNameCompanyDepartmentPipe } from "./shared/pipes/user-name-company-department";
import { ReadRecallModalComponent } from "@shared/readRecall-modal/readRecall-modal.component";
import { SendBackModalComponent } from "@shared/sendBack-modal/sendBack-modal.component";
import LeftPanelComponent from "@shared/leftPanel/leftPanel.component";
import RFIProcessingTabComponent from "@shared/rfiProcessingTab/rfiProcessingTab.component";
import RFILogTabComponent from "@shared/rfiLogTab/rfiLogTab.component";
import { RejectHistoryTableComponent } from "@shared/rejectHistoryTable/rejectHistoryTable.component";
import { RecallHistoryTableComponent } from "@shared/recallHistoryTable/recallHistoryTable.component";
import { WorkflowHistoryComponent } from "@shared/workflowHistory/workflowHistory.component";
import RFIResponseDetailComponent from "@shared/rfiResponse-detail/rfiResponse-detail.component";
import RFIHeaderComponent from "@shared/rfi-header/rfi-header.component";
import RFIDetailComponent from "@shared/rfi-detail/rfi-detail.component";
import ReopenDetailComponent from "@shared/reopen-detail/reopen-detail.component";
import { ReopenModalComponent } from "@shared/reopen-modal/reopen-modal.component";
import { ReopenHistoryTableComponent } from "@shared/reopenHistoryTable/reopenHistoryTable.component";
import {MatProgressBarModule} from '@angular/material/progress-bar';
import { UpdateDueDateModalComponent } from "@shared/updateDueDate-modal/updateDueDate-modal.component";
import LocalTaskService from "@services/local-task.service";
import RfiFileUrlProvider from "./services/fileUrl.service";

export function tokenGetter(): string {
  return localStorage.getItem(StorageKey.TOKEN);
}

export function loggerCallback(logLevel: LogLevel, message: string) {
  console.log(message);
}

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: OAuthSettings.appId,
      authority: OAuthSettings.authority,
      redirectUri: environment.url.redirectUri,
      postLogoutRedirectUri: environment.url.logoutURL,
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
    },
    system: {
      loggerOptions: {
        loggerCallback,
        logLevel: LogLevel.Info,
        piiLoggingEnabled: false
      },
      iframeBridgeTimeout: 10000,
    }
  });
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap: OAuthSettings.protectedResourceMap,
    // unprotectedResources: OAuthSettings.unprotectedResources,
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: OAuthSettings.scopes,
    },
    loginFailedRoute: environment.url.redirectUri,
  };
}


export function initializeMsal(msalService: MsalService): () => Promise<void> {
  return () => msalService.instance.initialize();
}

@NgModule({
  declarations: [
    AppComponent,
    AccessDeniedComponent,
    BaseComponent,
    ErrorComponent,
    CreateRfiComponent,
    ReviewRfiComponent,
    ViewRFIComponent,
    ReportComponent,
    RFIDetailComponent,
    RFIListComponent,
    CompilingRfiComponent,
    InreviewQaqcRfiComponent,
    RFIHeaderComponent,
    CompilingRfiComponent,
    DelegateModalComponent,
    RFIListComponent,
    RecallModalComponent,
    ReadRecallModalComponent,
    SendBackModalComponent,
    NotifyListComponent,
    SignoutComponent,
    UserNameCompanyDepartmentPipe,
    RFIResponseDetailComponent,
    LeftPanelComponent,
    RFIProcessingTabComponent,
    RFILogTabComponent,
    RejectHistoryTableComponent,
    RecallHistoryTableComponent,
    WorkflowHistoryComponent,
    ReopenDetailComponent,
    ReopenModalComponent,
    ReopenHistoryTableComponent,
    UpdateDueDateModalComponent,
  ],
  imports: [
    AecomCoreModule,
    BBJCoreModule,
    BrowserModule,
    CommonModule,
    AppRoutingModule,
    FormsModule,
    ReactiveFormsModule,
    HttpClientModule,
    NgxSpinnerModule,
    MsalModule,
    BrowserAnimationsModule,
    ApiModule,
    MatProgressBarModule,
  ],
  providers: [
    // Fresh SAS per click; without this the library falls back to the stored, expiring URLs.
    { provide: BBJFileUrlProvider, useExisting: RfiFileUrlProvider },
    PendingChangesGuard,
    BBJFileListService,
    BBJSelectUserService,
    LocalStorageService,
    LocalContractService,
    LocalClassificationService,
    LocalContractRequirementService,
    LocalSpecificationService,
    LocalContractUserService,
    LocalUserService,
    LocalTaskService,
    PreviousRouteService,
    AuthService,
    LayoutService,
    NavService,
    LoginUserResolve,
    ErrorResolve,
    RFIViewResolver,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    { provide: BASE_PATH, useValue: environment.baseWebAPI },
    MsalBroadcastService,
    MsalService,

    {
      provide: APP_INITIALIZER,
      useFactory: initializeMsal,
      deps: [MsalService],
      multi: true,
    },
    MsalGuard,
  ],
  bootstrap: [AppComponent, MsalRedirectComponent],
})
export class AppModule {}
